/* global
APP_NAME,
COMMIT_VERSION,
 */
import { getRuntimeConfig } from "cortex/ui/src/runtime/config";

// These globals are provided at build time.
declare const APP_NAME: string;
declare const COMMIT_VERSION: string;

/*
 * This module should stay top level and not import from the app,
 * as these constants are used by other top-level modules. For example
 * by the top-level Sentry integration.
 */
const LOCAL = "local" as const;
const DEVELOPMENT = "development" as const;
const PRODUCTION = "production" as const;
const STAGING = "staging" as const;

const ENVS = {
  LOCAL,
  DEVELOPMENT,
  PRODUCTION,
  STAGING,
} as const;

type DeployTarget = (typeof ENVS)[keyof typeof ENVS] | string;

// TODO: GET Fleet URLS from runtime environment variables.
function getFleetUrl(target: DeployTarget): string {
  switch (target) {
    case ENVS.LOCAL:
      return "https://fleet.local.cortex.vistarmedia.com";
    case ENVS.DEVELOPMENT:
      return "https://fleet.dev.cortex.vistarmedia.com";
    case ENVS.STAGING:
      return "https://fleet.staging.cortex.vistarmedia.com";
    default:
      return "https://fleet.cortexpowered.com";
  }
}

function getTraffickingUrl(target: DeployTarget): string {
  switch (target) {
    case ENVS.LOCAL:
      return "https://dev-trafficking.vistarmedia.com";
    case ENVS.DEVELOPMENT:
      return "https://dev-trafficking.vistarmedia.com";
    case ENVS.STAGING:
      return "https://staging-trafficking.vistarmedia.com";
    default:
      return "https://trafficking.vistarmedia.com";
  }
}

type RuntimeConfig = {
  DEPLOY_TARGET?: string;
  NODE_ENV?: string;
  SENTRY_DSN?: string;
};

const RUNTIME_CFG = getRuntimeConfig() as RuntimeConfig;
const RUNTIME_DEPLOY_TARGET: DeployTarget =
  RUNTIME_CFG.DEPLOY_TARGET || PRODUCTION;
const RUNTIME_NODE_ENV: string = RUNTIME_CFG.NODE_ENV || PRODUCTION;

const FLEET_URL = getFleetUrl(RUNTIME_DEPLOY_TARGET);
const TRAFFICKING_URL = getTraffickingUrl(RUNTIME_DEPLOY_TARGET);

const ENV = {
  APP_NAME,
  COMMIT_VERSION,
  // This specifies the deployment environment: staging, production.
  DEPLOY_TARGET: RUNTIME_DEPLOY_TARGET,
  FLEET_URL,
  // The builds are always "production" builds.
  NODE_ENV: RUNTIME_NODE_ENV,
  SENTRY_DSN: RUNTIME_CFG.SENTRY_DSN || "",
  TRAFFICKING_URL,
} as const;

// Indicate a cancelled request.
const STATUS_CODE_CANCEL = -1;

// Regex for validating the URL
const URL_PATTERN = /^https?:\/\/[A-Za-z0-9-._~:/?#[\]@!$&'()*+,;=%]+$/;

const KILOBYTE = 1024;
const MEGABYTE = 1024 * 1024;

const SIDEBAR_EXPANDED_WIDTH =
  parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--sidebar-expanded-width",
    ),
  ) || 224;

const SIDEBAR_COLLAPSED_WIDTH =
  parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--sidebar-collapsed-width",
    ),
  ) || 56;

// While the standard mimetype for csv is text/csv, these could also
// be valid csv files, depending on how the csv was created. This is not an
// complete list as none exist.
const POSSIBLE_CSV_MIMETYPES = [
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.msexcel",
  "application/x-csv",
  "text/anytext",
  "text/comma-separated-values",
  "text/csv",
  "text/plain",
  "text/tab-separated-values",
  "text/x-csv",
  "text/x-comma-separated-values",
] as const;

const USER_RE = {
  SYSTEM: /cortexsystem\+\d+@vistarmedia\.com/,
  VISTAR: /.+@vistarmedia\.com/,
} as const;

const ROLE = {
  ADMIN: "W",
  ADMIN_OPERATOR: "A",
  DISABLED: "D",
  OPERATOR: "O",
  READ_ONLY: "R",
  SUB_ACCOUNT: "S",
  UNKNOWN: "U",
} as const;

const RELEASE_NOTES_URL =
  "https://help.vistarmedia.com/hc/en-us/articles/45096025702541-2026-Cortex-Release-Notes";

export {
  ENV,
  ENVS,
  getTraffickingUrl,
  KILOBYTE,
  MEGABYTE,
  POSSIBLE_CSV_MIMETYPES,
  RELEASE_NOTES_URL,
  ROLE,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  STATUS_CODE_CANCEL,
  URL_PATTERN,
  USER_RE,
};
